import type { Map, MapMouseEvent } from 'mapbox-gl';
import {
  isTopDownPitch,
  QUICK_CAMERA_ANIMATION_DURATION_MS,
} from './helpers/map-perspective.helpers';
import {
  snapBearingToBuildingDirections,
  type BuildingBearingSnapResult,
} from './helpers/building-bearing-snap.helpers';

const LEFT_MOUSE_BUTTON = 0;
const RIGHT_MOUSE_BUTTON = 2;
const LEFT_MOUSE_BUTTON_FLAG = 1;
const RIGHT_MOUSE_BUTTON_FLAG = 2;
const DRAG_THRESHOLD_PX = 1;
const BEARING_DEGREES_PER_PIXEL = 0.8;
const PITCH_DEGREES_PER_PIXEL = -0.5;
export const FACADE_MODE_PITCH_DEGREES = 90;

export type DesignMapMouseNavigation = 'pan' | 'rotate';

/**
 * Design intentionally reverses Mapbox's desktop defaults: left-drag rotates
 * and right-drag pans. Ctrl explicitly forces view rotation with either button.
 */
export const resolveDesignMapMouseNavigation = (
  button: number,
  ctrlKey: boolean,
  spacePanActive = false,
): DesignMapMouseNavigation | undefined => {
  if (spacePanActive && button === LEFT_MOUSE_BUTTON) return 'pan';
  if (
    ctrlKey &&
    (button === LEFT_MOUSE_BUTTON || button === RIGHT_MOUSE_BUTTON)
  ) {
    return 'rotate';
  }
  if (button === LEFT_MOUSE_BUTTON) return 'rotate';
  if (button === RIGHT_MOUSE_BUTTON) return 'pan';
  return undefined;
};

type MouseNavigationCursorHandler = (cursor: 'grabbing' | undefined) => void;
type BearingSnapDirectionsProvider = () => readonly number[];
type FacadeModeHandler = (active: boolean) => void;
type FacadeTransitionProgressHandler = (progress: number) => void;
type FacadeTransitionEndHandler = () => void;
type FacadeVerticalPanHandler = (deltaYPixels: number) => void;
type FacadeVerticalPanEndHandler = () => void;

type ActiveMouseNavigation = {
  action: DesignMapMouseNavigation;
  button: number;
  startClient: { x: number; y: number };
  lastClient: { x: number; y: number };
  moved: boolean;
  rawBearing: number;
  rawPitch: number;
  bearingSnapState: BuildingBearingSnapResult['state'];
  bearingSnapDirections: readonly number[];
};

export const shouldSnapDesignMapBearing = (
  shiftKey: boolean,
  pitch: number,
): boolean => shiftKey && isTopDownPitch(pitch);

/** Keep geographic panning horizontal in facade mode; use screen Y separately. */
export const resolveDesignMapPanDrag = ({
  deltaX,
  deltaY,
  facadeModeActive,
}: {
  deltaX: number;
  deltaY: number;
  facadeModeActive: boolean;
}): { mapOffset: [number, number]; facadeVerticalDelta: number } => ({
  mapOffset: [-deltaX, facadeModeActive ? 0 : -deltaY],
  // Clip-space Y points upward while pointer Y points downward. Invert it so
  // facade panning feels like directly grabbing and dragging the view.
  facadeVerticalDelta: facadeModeActive ? -deltaY : 0,
});

export const resolveFacadePitchDrag = ({
  rawPitch,
  deltaYPx,
  minPitch,
  maxPitch,
  facadeModeActive,
}: {
  rawPitch: number;
  deltaYPx: number;
  minPitch: number;
  maxPitch: number;
  facadeModeActive: boolean;
}): {
  rawPitch: number;
  mapPitch: number;
  facadeModeActive: boolean;
  facadeTransitionProgress: number;
} => {
  const nextRawPitch = Math.min(
    FACADE_MODE_PITCH_DEGREES,
    Math.max(minPitch, rawPitch + deltaYPx * PITCH_DEGREES_PER_PIXEL),
  );
  const isIncreasingPitch = deltaYPx * PITCH_DEGREES_PER_PIXEL > 0;
  const nextFacadeModeActive = facadeModeActive
    ? nextRawPitch >= maxPitch
    : nextRawPitch >= maxPitch && isIncreasingPitch;
  const facadePitchRange = FACADE_MODE_PITCH_DEGREES - maxPitch;
  const facadeTransitionProgress = nextFacadeModeActive
    ? Math.min(1, Math.max(0, (nextRawPitch - maxPitch) / facadePitchRange))
    : 0;
  return {
    rawPitch: nextRawPitch,
    mapPitch: nextFacadeModeActive ? maxPitch : nextRawPitch,
    facadeModeActive: nextFacadeModeActive,
    facadeTransitionProgress,
  };
};

/**
 * Space-pan has absolute priority over object handlers and temporary drag-pan
 * locks. Ordinary navigation still respects prevented events and Mapbox state.
 */
export const canStartDesignMapMouseNavigation = ({
  action,
  eventDefaultPrevented,
  spacePanActive,
  modifierRotationActive,
  buildingRotationActive = false,
  templatePreviewActive = false,
  pointDragActive = false,

  dragPanEnabled,
  dragRotateEnabled,
}: {
  action: DesignMapMouseNavigation;
  eventDefaultPrevented: boolean;
  spacePanActive: boolean;
  modifierRotationActive?: boolean;
  buildingRotationActive?: boolean;
  templatePreviewActive?: boolean;
  pointDragActive?: boolean;
  dragPanEnabled: boolean;
  dragRotateEnabled: boolean;
}): boolean => {
  if (
    templatePreviewActive &&
    action === 'rotate' &&
    !modifierRotationActive &&
    !spacePanActive
  )
    return false;
  if ((buildingRotationActive || pointDragActive) && !spacePanActive)
    return false;

  if (eventDefaultPrevented && !spacePanActive && !modifierRotationActive) {
    return false;
  }
  if (action === 'pan') return spacePanActive || dragPanEnabled;
  return dragRotateEnabled;
};

/** Swaps Mapbox's desktop pan/rotate mouse buttons without changing touch. */
export class MouseNavigationController {
  private map: Map | undefined;
  private active: ActiveMouseNavigation | undefined;
  private onCursor: MouseNavigationCursorHandler | undefined;
  private getBearingSnapDirections: BearingSnapDirectionsProvider | undefined;
  private onFacadeModeChange: FacadeModeHandler | undefined;
  private onFacadeTransitionProgress:
    FacadeTransitionProgressHandler | undefined;
  private onFacadeTransitionEnd: FacadeTransitionEndHandler | undefined;
  private onFacadeVerticalPan: FacadeVerticalPanHandler | undefined;
  private onFacadeVerticalPanEnd: FacadeVerticalPanEndHandler | undefined;
  private facadeModeActive = false;
  private spacePanActive = false;
  private isBuildingRotationActive: (() => boolean) | undefined;
  private isPointDragActive: (() => boolean) | undefined;
  private isCtrlDrawingActive: (() => boolean) | undefined;
  private isTemplatePreviewActive: (() => boolean) | undefined;

  setTemplatePreviewActiveProvider(provider: () => boolean): void {
    this.isTemplatePreviewActive = provider;
  }

  setCtrlDrawingActiveProvider(provider: () => boolean): void {
    this.isCtrlDrawingActive = provider;
  }

  setBuildingRotationActiveProvider(provider: () => boolean): void {
    this.isBuildingRotationActive = provider;
  }

  setPointDragActiveProvider(provider: () => boolean): void {
    this.isPointDragActive = provider;
  }

  setCursorHandler(handler: MouseNavigationCursorHandler | undefined): void {
    this.onCursor = handler;
  }

  setBearingSnapDirectionsProvider(
    provider: BearingSnapDirectionsProvider | undefined,
  ): void {
    this.getBearingSnapDirections = provider;
  }

  setFacadeModeHandler(handler: FacadeModeHandler | undefined): void {
    this.onFacadeModeChange = handler;
  }

  setFacadeTransitionProgressHandler(
    handler: FacadeTransitionProgressHandler | undefined,
  ): void {
    this.onFacadeTransitionProgress = handler;
  }

  setFacadeTransitionEndHandler(
    handler: FacadeTransitionEndHandler | undefined,
  ): void {
    this.onFacadeTransitionEnd = handler;
  }

  setFacadeVerticalPanHandler(
    handler: FacadeVerticalPanHandler | undefined,
  ): void {
    this.onFacadeVerticalPan = handler;
  }

  setFacadeVerticalPanEndHandler(
    handler: FacadeVerticalPanEndHandler | undefined,
  ): void {
    this.onFacadeVerticalPanEnd = handler;
  }

  /** Keep gesture routing aligned when facade mode is restored externally. */
  setFacadeModeActive(active: boolean): void {
    this.facadeModeActive = active;
  }

  /** Temporarily make left-drag pan while Space is held. */
  setSpacePanActive(active: boolean): void {
    this.spacePanActive = active;
  }

  attach(map: Map): void {
    this.detach();
    this.map = map;
    map.on('mousedown', this.handleMouseDown);
    window.addEventListener('mousemove', this.handleMouseMove);
    window.addEventListener('mouseup', this.handleMouseUp);
    window.addEventListener('blur', this.handleBlur);
  }

  detach(): void {
    this.map?.off('mousedown', this.handleMouseDown);
    window.removeEventListener('mousemove', this.handleMouseMove);
    window.removeEventListener('mouseup', this.handleMouseUp);
    window.removeEventListener('blur', this.handleBlur);
    this.endNavigation(false);
    if (this.facadeModeActive) this.onFacadeModeChange?.(false);
    this.facadeModeActive = false;
    this.map = undefined;
  }

  private readonly handleMouseDown = (event: MapMouseEvent): void => {
    if (this.active || !this.map) return;
    const originalEvent = event.originalEvent;
    const action = resolveDesignMapMouseNavigation(
      originalEvent.button,
      originalEvent.ctrlKey,
      this.spacePanActive,
    );
    if (!action) return;
    const eventWasDefaultPrevented = event.defaultPrevented;

    // Prevent Mapbox's built-in handlers; Design owns the swapped buttons and
    // the Ctrl override itself.
    event.preventDefault();
    if (
      !canStartDesignMapMouseNavigation({
        action,
        eventDefaultPrevented: eventWasDefaultPrevented,
        spacePanActive: this.spacePanActive,
        modifierRotationActive: originalEvent.ctrlKey && action === 'rotate',
        buildingRotationActive:
          this.isBuildingRotationActive?.() ||
          (originalEvent.ctrlKey &&
            originalEvent.button === LEFT_MOUSE_BUTTON &&
            Boolean(this.isCtrlDrawingActive?.())),
        templatePreviewActive: this.isTemplatePreviewActive?.(),
        pointDragActive: this.isPointDragActive?.(),

        dragPanEnabled: this.map.dragPan.isEnabled(),
        dragRotateEnabled: this.map.dragRotate.isEnabled(),
      })
    ) {
      return;
    }
    const client = {
      x: originalEvent.clientX,
      y: originalEvent.clientY,
    };
    this.active = {
      action,
      button: originalEvent.button,
      startClient: client,
      lastClient: client,
      moved: false,
      rawBearing: this.map.getBearing(),
      rawPitch: this.facadeModeActive
        ? FACADE_MODE_PITCH_DEGREES
        : this.map.getPitch(),
      bearingSnapState: {},
      bearingSnapDirections: this.getBearingSnapDirections?.() ?? [],
    };
  };

  private readonly handleMouseMove = (event: MouseEvent): void => {
    const active = this.active;
    const map = this.map;
    if (!active || !map) return;
    if (!this.isButtonStillPressed(event, active.button)) {
      this.endNavigation();
      return;
    }

    const dx = event.clientX - active.lastClient.x;
    const dy = event.clientY - active.lastClient.y;
    if (!active.moved) {
      const totalDx = event.clientX - active.startClient.x;
      const totalDy = event.clientY - active.startClient.y;
      if (Math.hypot(totalDx, totalDy) < DRAG_THRESHOLD_PX) return;
      active.moved = true;
      if (active.action === 'pan') this.onCursor?.('grabbing');
    }

    event.preventDefault();
    active.lastClient = { x: event.clientX, y: event.clientY };
    if (active.action === 'pan') {
      const pan = resolveDesignMapPanDrag({
        deltaX: dx,
        deltaY: dy,
        facadeModeActive: this.facadeModeActive,
      });
      if (pan.mapOffset[0] !== 0 || pan.mapOffset[1] !== 0) {
        map.panBy(pan.mapOffset, { animate: false });
      }
      if (pan.facadeVerticalDelta !== 0) {
        this.onFacadeVerticalPan?.(pan.facadeVerticalDelta);
      }
      return;
    }

    active.rawBearing += dx * BEARING_DEGREES_PER_PIXEL;
    const pitch = resolveFacadePitchDrag({
      rawPitch: active.rawPitch,
      deltaYPx: dy,
      minPitch: map.getMinPitch(),
      maxPitch: map.getMaxPitch(),
      facadeModeActive: this.facadeModeActive,
    });
    active.rawPitch = pitch.rawPitch;
    if (pitch.facadeModeActive !== this.facadeModeActive) {
      this.facadeModeActive = pitch.facadeModeActive;
      this.onFacadeModeChange?.(pitch.facadeModeActive);
    }
    this.onFacadeTransitionProgress?.(pitch.facadeTransitionProgress);
    const shouldSnapBearing = shouldSnapDesignMapBearing(
      event.shiftKey,
      pitch.mapPitch,
    );
    const bearing = shouldSnapBearing
      ? snapBearingToBuildingDirections(
          active.rawBearing,
          active.bearingSnapDirections,
          active.bearingSnapState,
        )
      : {
          bearing: active.rawBearing,
          snapped: false,
          state: {},
        };
    active.bearingSnapState = bearing.state;
    map.jumpTo({ bearing: bearing.bearing, pitch: pitch.mapPitch });
  };

  private readonly handleMouseUp = (event: MouseEvent): void => {
    if (event.button !== this.active?.button) return;
    this.endNavigation();
  };

  private readonly handleBlur = (): void => {
    this.endNavigation();
  };

  private isButtonStillPressed(event: MouseEvent, button: number): boolean {
    const flag =
      button === LEFT_MOUSE_BUTTON
        ? LEFT_MOUSE_BUTTON_FLAG
        : RIGHT_MOUSE_BUTTON_FLAG;
    return event.buttons === undefined || (event.buttons & flag) === flag;
  }

  private endNavigation(finishFacadeTransition = true): void {
    if (!this.active) return;
    const hadPanCursor = this.active.moved && this.active.action === 'pan';
    const shouldPersistFacadePan = hadPanCursor && this.facadeModeActive;
    const shouldFinishFacadeTransition =
      this.active.moved &&
      this.active.action === 'rotate' &&
      this.facadeModeActive &&
      finishFacadeTransition;
    const pitch = this.map?.getPitch();
    const shouldSettleTopDown =
      finishFacadeTransition &&
      this.active.moved &&
      this.active.action === 'rotate' &&
      !this.facadeModeActive &&
      pitch !== undefined &&
      pitch > 0 &&
      pitch < 10;
    this.active = undefined;
    if (shouldSettleTopDown) {
      this.map?.easeTo({
        pitch: 0,
        duration: QUICK_CAMERA_ANIMATION_DURATION_MS,
      });
    }
    if (hadPanCursor) this.onCursor?.(undefined);
    if (shouldPersistFacadePan) this.onFacadeVerticalPanEnd?.();
    if (shouldFinishFacadeTransition) this.onFacadeTransitionEnd?.();
  }
}
